import { ReactNode } from 'react';
import { ChevronRight, ExternalLink } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { assetUrl } from '../../Shared/helpers';
import { useLocale } from '../../Shared/locale';
import { Charity, Event, Pagination, ProjectType } from '../../Shared/types';
import { Card } from '../../Shared/UI/Card';
import { Icon } from '../../Shared/UI/Icon';
import { Pager } from '../../Shared/UI/Pager';
import { SearchFilterInput } from '../../Shared/UI/SearchFilterInput';
import EventHeader from '../Event/EventHeader';
import { useRouter } from '../router';
import { SharedProps } from '../types';
import Container from '../UI/Container';
import { Footer } from '../UI/Footer';
import Header from '../UI/Header';
import Layout from '../UI/Layout';
import { NavBar } from '../UI/NavBar';

export interface SelectCharityPageProps extends SharedProps {
  event: (Event & {
    funds_raised: number;
    campaigns_count: number;
    charities_count: number;
  }) | null;
  charities: Pagination<Charity>;
}

export default function SelectCharityPage({
  event,
  charities,
  auth: {
    user,
  },
}: SelectCharityPageProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  return (
    <>
      <NavBar user={user} showCampaignButton={false} />

      {event && (
        <Header className="bg-slate-50 border-t">
          <Container>
            <EventHeader event={event} />
          </Container>
        </Header>
      )}

      <Container>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl">
            {t('frontend:start_a_campaign')}
          </h1>
          <span className="text-sm whitespace-nowrap text-slate-500">
            {t('frontend:step_x_of_y', { x: 1, y: 3 })}
          </span>
        </div>

        <div>
          <div className="p-4 bg-blue-100 sm:p-8 rounded-xl">
            <img
              src={assetUrl('images/website/homepage-step-2.svg')}
              alt={t('frontend:homepage.create_a_campaign')}
              className="max-w-[200px] mx-auto -mb-2"
            />
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <h3 className="text-base">
              {t('frontend:select_your_charity')}
            </h3>
            <p>
              {t('frontend:select_your_charity_description')}
            </p>
          </div>

          <div>
            <SearchFilterInput
              id="search"
              inputSize="lg"
              className="!rounded-lg"
              placeholder={t('frontend:find_your_charity')}
            />
          </div>

          <div className="space-y-2">
            {charities.data.map((charity) => (
              <CharityCard charity={charity} event={event} key={charity.id} />
            ))}
          </div>

          {charities.data.length === 0 && (
            <div className="text-slate-500">
              {t('frontend:no_results')}
            </div>
          )}
        </div>

        {charities.meta.last_page > 1 && (
          <Pager data={charities} preserveScroll={false} />
        )}

        <div className="text-center">
          <a
            href={`https://about.supporta.com/${locale}/submit-charity`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('frontend:charity_missing')}
            <Icon className="ml-1">
              <ExternalLink />
            </Icon>
          </a>
        </div>
      </Container>

      <Footer />
    </>
  );
}

SelectCharityPage.layout = (page: ReactNode) => <Layout>{page}</Layout>;

const CharityCard = ({
  charity,
  event,
}: {
  charity: Charity;
  event: Event | null;
}) => {
  const { routes } = useRouter();

  return (
    <Card
      spacing="xs"
      link={{
        href: routes.create_campaign_page(charity.id, event ? ProjectType.Event : null, { eventId: event?.id }),
        ariaLabel: charity.title,
      }}
      border
      className="flex items-center gap-4 !py-3 !rounded-lg"
    >
      <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 p-1 rounded">
        {charity.logo_url && (
          <img src={charity.logo_url} alt={charity.title} className="block object-contain w-full h-full" />
        )}
        {!charity.logo_url && (
          <span className="block w-10 h-10 rounded-full leading-[2.5rem] text-center bg-blue-300 text-white">
            {charity.title.slice(0, 1)}
          </span>
        )}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="overflow-hidden whitespace-nowrap text-ellipsis">
            {charity.title}
          </h4>
          <Icon className="text-slate-400">
            <ChevronRight />
          </Icon>
        </div>
        <div className="w-full text-slate-500 line-clamp-2">
          {charity.description}
        </div>
      </div>
    </Card>
  );
};
